import sparrows001 from "@/assets/works/a-house-for-sparrows/sparrows-001.webp"
import sparrows002 from "@/assets/works/a-house-for-sparrows/sparrows-002.webp"
import sparrows003 from "@/assets/works/a-house-for-sparrows/sparrows-003.webp"
import byg001 from "@/assets/works/bring-your-garden/byg-001.webp"
import byg002 from "@/assets/works/bring-your-garden/byg-002.webp"
import byg003 from "@/assets/works/bring-your-garden/byg-003.webp"
import byg004 from "@/assets/works/bring-your-garden/byg-004.webp"
import void000 from "@/assets/works/void-chair/void-000.webp"
import void003 from "@/assets/works/void-chair/void-003.webp"
import void009 from "@/assets/works/void-chair/void-009.webp"

export type WorkPhoto = {
  url: string
  alt: string
}

export type PortfolioWork = {
  slug: string
  title: string
  year: string
  /** Short lede under the title (detail page only); not part of body `description`. */
  tagline: string
  thumbnailUrl: string
  description: string[]
  photos: WorkPhoto[]
  dimensions?: string
}

/**
 * Featured portfolio works — copy sourced from `src/app/mocks/mock.md`.
 * Replace images under `src/assets/works/<slug>/` — first imported image is the listing thumbnail and first gallery slot (Void Chair uses `void-000.webp`; others often `-001.webp`); then `002`, ….
 * Run `bun run assets:webp` after adding JPEGs to convert (see `scripts/convert-assets-to-webp.ts`).
 */
export const PORTFOLIO_WORKS: PortfolioWork[] = [
  {
    slug: "a-house-for-sparrows",
    title: "A House For Sparrows",
    year: "2026",
    tagline: "Designed from the sparrow's point of view.",
    thumbnailUrl: sparrows001,
    description: [
      "Sparrows live on the boundary between the city and the wild — neither domesticated nor fully free, in a world built without them in mind. Daily life is shared across species, but almost always designed for only one.",
      "A sparrow will not use a house that does not meet its needs. No compromise, no politeness — the most honest user I have designed for. I studied how they read enclosure, interior temperature, and entrance size. Every formal decision came from that research, not from aesthetics: an asymmetric roof drains rain faster; an obtuse hexagon gives the sense of closure they respond to; fewer side openings keep heat stable.",
      "I stopped designing from my own logic and started designing from theirs. Interior temperature was measured across softwood and hardwood outdoors to choose material that holds the conditions they need. Paper, wood, plastic, and more were tried; a predator-sensing hole was abandoned when it hurt temperature and safety. Models and final pieces were cut, assembled, and re-checked against the data.",
    ],
    photos: [
      { url: sparrows001, alt: "Sparrow house exterior form" },
      { url: sparrows002, alt: "Hexagonal enclosure study" },
      { url: sparrows003, alt: "Models and material tests" },
    ],
  },
  {
    slug: "void-chair",
    title: "Void Chair",
    year: "2025",
    tagline: "The gap is where everything essential happens.",
    thumbnailUrl: void000,
    description: [
      "I am drawn to ambiguous gaps — the space between a door and its frame, the silence before a reply. Not as absence, but as the place where attention gathers.",
      "The character 間 was born from sunlight passing through a gate: gap, interval, between. Time is the flow between moments; space is the void between objects. The gap is not lack — it is where everything essential takes place.",
      "This chair makes that visible: intentional steps and gaps between seat, back, and leg, where interpretation can rest instead of being sealed tight. Bent plywood keeps discontinuity structural and visible — physical gap, room for meaning, contact that stays loose and sensitive. Prototypes at different scales and measured data shaped the backrest before it was joined to the other parts.",
    ],
    photos: [
      { url: void000, alt: "Void Chair full view" },
      { url: void009, alt: "Gaps between seat, back, and leg" },
      { url: void003, alt: "Plywood forming and prototypes" },
    ],
  },
  {
    slug: "bring-your-garden",
    title: "Bring Your Garden",
    year: "2026",
    tagline: "A trolley designed for both the human and the plant.",
    thumbnailUrl: byg001,
    description: [
      "Moving heavy pots is often what turns the joy of growing plants into a burden. Bring Your Garden is a trolley meant to remove that friction — letting the garden move through the home.",
      "The height humans reach comfortably and the height plants need for light are not the same; three tiers address both at once. A unified wood surface quiets the visual noise of mismatched pots. The rear compartment sits deep and low so heavy pots stay near the axle and the center of gravity stays stable as it moves.",
      "Almost the entire piece is wood, including the wheels, for warmth next to living plants. Tier heights were set from how people naturally reach, then tested through repeated prototyping until the form felt right. Dozens of drawings explored weight, height, and movement before the three-tier structure settled.",
      "Curvature in wood required a custom mold: layers glued and pressed into laminated plywood under pressure. A trolley, a shelf, a garden, an object — all four at once, moving the garden through the home while respecting where people reach and where plants thrive.",
    ],
    photos: [
      { url: byg001, alt: "Three-tier garden trolley" },
      { url: byg002, alt: "Trolley in use with plants" },
      { url: byg003, alt: "Lamination and mold process" },
      { url: byg004, alt: "Form development sketches" },
    ],
  },
]

const bySlug = Object.fromEntries(PORTFOLIO_WORKS.map((w) => [w.slug, w]))

export function getWorkBySlug(slug: string | undefined): PortfolioWork | undefined {
  if (!slug) return undefined
  return bySlug[slug]
}
